import { config } from "../config";
import { getAuthToken } from "./authService";

// Interface for the upload response
export interface UploadResponse {
  success: boolean;
  message: string;
  documentId?: string;
  fileName?: string;
  successCount?: number;
  failureCount?: number;
}

// Interface for document data returned by API
export interface Document {
  id: string;
  file_name: string;
  created_at: string;
  purpose: string;
  status: string;
}

// Interface for paginated document list
export interface DocumentsResponse {
  data: Document[];
  total_count: number;
  has_more: boolean;
  object: string;
  next_cursor?: string;
  page_size: number;
  count: number;
}

/**
 * Delete a document from the knowledge base
 *
 * @param documentId ID of the document to delete (in format vesf_xxx)
 * @returns Success status and any error message
 */
export const deleteDocument = async (
  documentId: string,
): Promise<{ success: boolean; message?: string }> => {
  // Get token from Okta auth
  let token;
  try {
    token = await getAuthToken();

    if (!token) {
      return {
        success: false,
        message: "Authentication token not available",
      };
    }
  } catch (authError) {
    console.error("Authentication error:", authError);
    return {
      success: false,
      message: "Failed to obtain authentication token. Please log in again.",
    };
  }

  // Build the base URL
  let baseUrl = config.api.ragUploadEndpoint || "";

  if (baseUrl.includes("{vectorStoreId}") && config.api.vectorStoreId) {
    baseUrl = baseUrl.replace("{vectorStoreId}", config.api.vectorStoreId);
  }

  // Remove the "/documents" part as we'll use it in a different way
  baseUrl = baseUrl.split("/documents")[0];

  try {
    const response = await fetch(`${baseUrl}/documents/${documentId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to delete document: ${response.status} ${response.statusText} ${errorText}`,
      );
    }

    return { success: true };
  } catch (error) {
    console.error("Error deleting document:", error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Unknown error occurred while deleting document",
    };
  }
};

/**
 * Fetch existing documents from the knowledge base with pagination support
 *
 * @param limit Number of documents to fetch per page
 * @param cursor Cursor for pagination
 * @returns DocumentsResponse containing documents and pagination details
 */
export const fetchDocuments = async (
  limit: number = 10,
  cursor?: string,
): Promise<DocumentsResponse> => {
  // Get token from Azure AD auth
  const token = await getAuthToken();

  if (!token) {
    throw new Error("Authentication token not available");
  }

  // Build the base URL
  let baseUrl = config.api.ragUploadEndpoint || "";

  if (baseUrl.includes("{vectorStoreId}") && config.api.vectorStoreId) {
    baseUrl = baseUrl.replace("{vectorStoreId}", config.api.vectorStoreId);
  }

  // Remove the "/documents" part as we'll use it in a different way
  baseUrl = baseUrl.split("/documents")[0];

  // Build the query parameters
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  params.append("includeTotal", "true");

  if (cursor) {
    params.append("after", cursor);
  }

  // Make the API request
  try {
    const response = await fetch(`${baseUrl}/documents?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch documents: ${response.status} ${response.statusText} ${errorText}`,
      );
    }

    const data = await response.json();
    return data as DocumentsResponse;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};

/**
 * Upload multiple documents to the knowledge base, processing each file with a separate API call
 *
 * @param files Array of Files to upload (PDF and DOCX only)
 * @returns Response with success/failure and details
 */
export const uploadDocuments = async (
  files: File[],
): Promise<UploadResponse> => {
  // Get token from Azure AD auth
  const token = await getAuthToken();

  if (!token) {
    return {
      success: false,
      message: "Authentication token not available",
    };
  }

  // Filter out unsupported file types
  const supportedFiles = files.filter(
    (file) =>
      file.type.includes("pdf") ||
      file.name.endsWith(".docx") ||
      file.name.endsWith(".doc"),
  );

  // Check if we have any supported files
  if (supportedFiles.length === 0) {
    return {
      success: false,
      message:
        "No supported files to upload. Only PDF and DOCX files are supported.",
    };
  }

  // Check file sizes (25MB max per file)
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
  const oversizedFiles = supportedFiles.filter(
    (file) => file.size > MAX_FILE_SIZE,
  );

  if (oversizedFiles.length > 0) {
    const validFiles = supportedFiles.filter(
      (file) => file.size <= MAX_FILE_SIZE,
    );
    if (validFiles.length === 0) {
      return {
        success: false,
        message: `All files exceed the 25MB limit and cannot be uploaded`,
      };
    }
    // Continue with valid files but note some were skipped
  }

  // Upload each file separately
  const results = {
    successCount: 0,
    failureCount: 0,
    fileResults: [] as Array<{
      fileName: string;
      success: boolean;
      message: string;
    }>,
  };

  // Get the upload endpoint from config and replace the vectorStoreId placeholder
  let uploadEndpoint = config.api.ragUploadEndpoint || "";

  if (uploadEndpoint.includes("{vectorStoreId}") && config.api.vectorStoreId) {
    uploadEndpoint = uploadEndpoint.replace(
      "{vectorStoreId}",
      config.api.vectorStoreId,
    );
  }

  // Process each file individually
  for (const file of supportedFiles) {
    // Skip oversized files
    if (file.size > MAX_FILE_SIZE) {
      results.failureCount++;
      results.fileResults.push({
        fileName: file.name,
        success: false,
        message: "File exceeds 25MB size limit",
      });
      continue;
    }

    try {
      // Create new FormData for each file
      const formData = new FormData();
      formData.append("files", file);

      // Send request for this individual file
      const response = await fetch(uploadEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        results.successCount++;
        results.fileResults.push({
          fileName: file.name,
          success: true,
          message: "File uploaded successfully",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        results.failureCount++;
        results.fileResults.push({
          fileName: file.name,
          success: false,
          message:
            errorData.message ||
            `Upload failed with status: ${response.status}`,
        });
      }
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      results.failureCount++;
      results.fileResults.push({
        fileName: file.name,
        success: false,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
    }
  }

  // Determine overall success/failure
  if (results.successCount > 0 && results.failureCount === 0) {
    return {
      success: true,
      message:
        results.successCount === 1
          ? `File uploaded successfully`
          : `All ${results.successCount} files uploaded successfully`,
      successCount: results.successCount,
      failureCount: results.failureCount,
    };
  } else if (results.successCount > 0 && results.failureCount > 0) {
    return {
      success: true,
      message: `${results.successCount} out of ${results.successCount + results.failureCount} files uploaded successfully`,
      successCount: results.successCount,
      failureCount: results.failureCount,
    };
  } else {
    return {
      success: false,
      message:
        results.failureCount === 1
          ? `Failed to upload file: ${results.fileResults[0].message}`
          : `Failed to upload all ${results.failureCount} files`,
      successCount: results.successCount,
      failureCount: results.failureCount,
    };
  }
};
